import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as XLSX from 'xlsx';
import Logo from './Logo';
import { brandIdentity } from '../theme/brandIdentity';

// Modal simplificado para carga de dispositivos desde Excel (sin tipos de bono)
function SimpleExcelDeviceModal({ isOpen, onClose, onFinalize }) {
  const [step, setStep] = useState('upload');
  const [dragActive, setDragActive] = useState(false);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationData, setValidationData] = useState([]);
  const [excelWorkbook, setExcelWorkbook] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');

  const requiredFields = ['name', 'bonusValue']; // Campos simples
  const fieldLabels = {
    name: 'Nombre del dispositivo',
    bonusValue: 'Valor del bono'
  };

  const processExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetNames = workbook.SheetNames;
        
        setExcelWorkbook(workbook);
        setExcelSheets(sheetNames);
        
        if (sheetNames.length === 1) {
          processSheet(workbook, sheetNames[0]);
        } else {
          setStep('sheet-selection');
        }
      } catch (error) {
        alert('Error al procesar el archivo Excel');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const processSheet = (workbook, sheetName) => {
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    
    const headers = jsonData[0] || [];
    const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));
    
    setExcelHeaders(headers);
    setExcelData(dataRows);
    setSelectedSheet(sheetName);
    setStep('mapping');
  };

  const validateAndFinalize = () => {
    const devices = excelData.map(row => ({
      name: row[columnMapping.name] || '',
      bonus_amount: parseInt(row[columnMapping.bonusValue]) || 0
    })).filter(device => device.name && device.bonus_amount > 0);

    onFinalize(devices);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="modal-content" style={{ background: 'white', borderRadius: '8px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Cargar Dispositivos desde Excel</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>

        {step === 'upload' && (
          <div>
            <p>Selecciona un archivo Excel con dispositivos (columnas: Nombre, Valor):</p>
            <div 
              style={{ 
                border: dragActive ? '2px solid #6366f1' : '2px dashed #d0d0d0', 
                borderRadius: '8px', 
                padding: '2rem', 
                textAlign: 'center', 
                cursor: 'pointer',
                backgroundColor: dragActive ? '#f8f9ff' : 'white'
              }}
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                if (e.dataTransfer.files[0]) processExcelFile(e.dataTransfer.files[0]);
              }}
            >
              <div>📊</div>
              <p>Arrastra tu archivo aquí o <label style={{ color: '#6366f1', cursor: 'pointer', textDecoration: 'underline' }}>
                haz clic para seleccionar
                <input type="file" accept=".xlsx,.xls" onChange={(e) => e.target.files[0] && processExcelFile(e.target.files[0])} style={{ display: 'none' }} />
              </label></p>
            </div>
          </div>
        )}

        {step === 'sheet-selection' && (
          <div>
            <h4>Seleccionar Hoja</h4>
            <div style={{ display: 'grid', gap: '10px' }}>
              {excelSheets.map(sheet => (
                <button key={sheet} onClick={() => processSheet(excelWorkbook, sheet)} 
                  style={{ padding: '12px', border: '1px solid #d0d0d0', borderRadius: '4px', cursor: 'pointer', textAlign: 'left' }}>
                  📄 {sheet}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'mapping' && (
          <div>
            <h4>Mapear Columnas</h4>
            {requiredFields.map(field => (
              <div key={field} style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{fieldLabels[field]}:</label>
                <select 
                  value={columnMapping[field] || ''} 
                  onChange={(e) => setColumnMapping(prev => ({ ...prev, [field]: e.target.value }))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d0d0d0', borderRadius: '4px' }}
                >
                  <option value="">Seleccionar columna...</option>
                  {excelHeaders.map((header, index) => (
                    <option key={index} value={index}>{header}</option>
                  ))}
                </select>
              </div>
            ))}
            <button 
              onClick={validateAndFinalize}
              disabled={!requiredFields.every(field => columnMapping[field] !== undefined)}
              style={{ 
                background: '#6366f1', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                padding: '10px 20px', 
                cursor: 'pointer',
                opacity: requiredFields.every(field => columnMapping[field] !== undefined) ? 1 : 0.5
              }}
            >
              Importar Dispositivos
            </button>
          </div>
        )}

        <div style={{ marginTop: '1rem', textAlign: 'right' }}>
          <button onClick={onClose} style={{ background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextToken, setNextToken] = useState(''); // Estado para el token de paginación
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Estado para carga de paginación
  const [expandedRows, setExpandedRows] = useState({}); // Para controlar las filas expandidas
  
  // Estados para edición y eliminación
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState('');
  const [operationSuccess, setOperationSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Estados para modal de Excel
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [editingCampaignExcel, setEditingCampaignExcel] = useState(null);
  const [showConfirmExcelModal, setShowConfirmExcelModal] = useState(false);
  
  // Estados para colecciones de bonos
  const [bonusCollections, setBonusCollections] = useState([]);
  const [loadingBonusCollections, setLoadingBonusCollections] = useState(false);

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  const tenantId = localStorage.getItem('tenant_id') || '';

  // Función para obtener las campañas
  const fetchCampaigns = async (paginationToken = '') => {
    if (!tenantId) {
      setError('No se encontró el ID del tenant. Por favor, inicia sesión de nuevo.');
      setLoading(false);
      setIsLoadingMore(false);
      return;
    }
    
    if (paginationToken) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    const params = {
      mode: 1, // Modo 1 para obtener listado
      tenant_id: tenantId,
      source: "web",
      get_type: "ALL", // Por ahora, siempre pedimos todas
      pagination_token: paginationToken
    };

    try {
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/campaigns',
        { 
          headers: await getAuthHeaders(),
          params: params
        }
      );

      if (response.status === 200 && response.data) {
        const newCampaigns = response.data.campaigns || [];
        const nextPaginationToken = response.data.next_pagination_token || '';
        
        // Si es la primera carga, reemplazamos. Si es "cargar más", concatenamos.
        setCampaigns(prevCampaigns => 
          paginationToken ? [...prevCampaigns, ...newCampaigns] : newCampaigns
        );
        setNextToken(nextPaginationToken);
      } else {
        setError('No se pudieron obtener las campañas.');
      }
    } catch (err) {
      console.error('Error al obtener campañas:', err);
      setError('Error al obtener las campañas: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Cargar campañas iniciales al montar el componente
  useEffect(() => {
    fetchCampaigns(); // Llamar sin token para la primera página
    fetchBonusCollections(); // Cargar colecciones de bonos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo se ejecuta una vez al montar

  const handleCreateCampaign = () => {
    navigate('/crear-campana');
  };

  // Función para manejar el botón "Cargar más"
  const handleLoadMore = () => {
    if (nextToken) {
      fetchCampaigns(nextToken);
    }
  };

  // Función para formatear fechas desde epoch (timestamp en milisegundos o segundos)
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    
    try {
      // Si es un número, lo tratamos como epoch
      if (typeof timestamp === 'number' || !isNaN(Number(timestamp))) {
        const epoch = Number(timestamp);
        // Verificar si es en segundos (10 dígitos) o milisegundos (13 dígitos)
        const milliseconds = epoch < 10000000000 ? epoch * 1000 : epoch;
        
        // Crear la fecha usando UTC para evitar problemas de zona horaria
        const date = new Date(milliseconds);
        
        if (isNaN(date.getTime())) {
          return '-';
        }
        
        // Formatear usando los métodos UTC
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // +1 porque los meses comienzan en 0
        const year = date.getUTCFullYear();
        
        return `${day}/${month}/${year}`;
      } 
      
      // Si es string, intentamos parsearlo como fecha normal
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      // Formatear usando los métodos UTC
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // +1 porque los meses comienzan en 0
      const year = date.getUTCFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (e) {
      console.error('Error al formatear fecha:', e);
      return '-';
    }
  };

  // Función para formatear el valor del bono
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('es-CO', { 
      style: 'currency', 
      currency: 'COP',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Función para alternar la expansión de una fila
  const toggleRowExpansion = (campaignId) => {
    setExpandedRows(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
  };

  // Función para iniciar la edición de una campaña
  const startEditing = (campaign, campaignId) => {
    setEditingCampaign(campaignId);
    setEditFormData({
      title: campaign.title || '',
      description: campaign.description || '',
      start_date: campaign.start_date || '',
      end_date: campaign.end_date || '',
      applicable_devices: campaign.applicable_devices ? [...campaign.applicable_devices] : [],
      bonus_collection_id: campaign.bonus_collection_id || '',
      bonus_collection_name: campaign.bonus_collection_name || ''
    });
    setOperationError('');
    setValidationErrors({});
  };

  // Función auxiliar para obtener la campaña original
  const getOriginalCampaign = (campaignId) => {
    return campaigns.find(c => c.campaign_id === campaignId);
  };

  // Función para cancelar la edición
  const cancelEditing = () => {
    setEditingCampaign(null);
    setEditFormData({});
    setOperationError('');
    setOperationSuccess('');
    setValidationErrors({});
  };

  // Función para actualizar una campaña
  const updateCampaign = async (campaignId) => {
    // Validaciones
    if (!campaignId || !editFormData.title.trim()) {
      setOperationError('El título es obligatorio.');
      return;
    }
    
    // Validar fechas
    if (editFormData.start_date && editFormData.end_date) {
      const startDate = new Date(editFormData.start_date * 1000);
      const endDate = new Date(editFormData.end_date * 1000);
      if (startDate >= endDate) {
        setOperationError('La fecha de inicio debe ser menor que la fecha de fin.');
        return;
      }
    }

    console.log('Edit Form Data:', editFormData);

    // Obtener la campaña original para validaciones
    const originalCampaign = campaigns.find(c => c.campaign_id === campaignId);

    // Validar dispositivos
    if (originalCampaign?.bonus_type === 'asignar_por_modelo') {
      const hasInvalidDevices = editFormData.applicable_devices.some(device => 
        !device.name.trim());
      if (hasInvalidDevices) {
        setOperationError('Todos los dispositivos deben tener nombre.');
        return;
      }
    } else {
      const hasInvalidDevices = editFormData.applicable_devices.some(device => 
        !device.name.trim() || device.bonus_amount <= 0
      );
      if (hasInvalidDevices) {
        setOperationError('Todos los dispositivos deben tener nombre y bono mayor a 0.');
        return;
      }
    }

    setOperationLoading(true);
    setOperationError('');
    setOperationSuccess('');
    
    try {
      const headers = await getAuthHeaders();
      
      // Obtener la campaña original para comparar
      const originalCampaign = campaigns.find(c => c.campaign_id === campaignId);
      
      // Preparar el body para la actualización
      const updates = {
        title: editFormData.title.trim(),
        description: editFormData.description.trim()
      };
      
      // Solo incluir start_date si se proporcionó un valor
      if (editFormData.start_date) {
        updates.start_date = parseInt(editFormData.start_date);
      }
      
      // Solo incluir end_date si se proporcionó un valor
      if (editFormData.end_date) {
        // Configurar la fecha de fin a las 23:59:59 del día seleccionado
        const endDate = new Date(editFormData.end_date * 1000);
        endDate.setHours(23, 59, 59, 999);
        updates.end_date = Math.floor(endDate.getTime() / 1000);
      }
      
      // Verificar si los dispositivos han cambiado
      const devicesChanged = JSON.stringify(editFormData.applicable_devices) !== 
                           JSON.stringify(originalCampaign?.applicable_devices || []);
      
      if (devicesChanged && editFormData.applicable_devices.length > 0) {
        updates.applicable_devices = editFormData.applicable_devices.map(device => {
          const deviceObj = {
            name: device.name.trim(),
            bonus_amount: parseInt(device.bonus_amount)
          };
          
          // Solo agregar código si es asignar por modelo
          if (originalCampaign?.bonus_type === 'asignar_por_modelo') {
            deviceObj.bonus_code = device.bonus_code || '';
          }
          
          return deviceObj;
        });
      }
      
      // Verificar si la colección de bonos ha cambiado para campañas de tipo catálogo por precios
      if (originalCampaign?.bonus_type === 'asignar_catalogo_precios') {
        if (editFormData.bonus_collection_id !== originalCampaign.bonus_collection_id) {
          updates.bonus_collection_id = editFormData.bonus_collection_id;
          updates.bonus_collection_name = editFormData.bonus_collection_name;
        }
      }
      
      const requestBody = {
        campaign_id: campaignId,
        updates: updates
      };

      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/campaigns',
        requestBody,
        { headers }
      );

      if (response.status === 200) {
        // Actualizar la campaña en el estado local
        setCampaigns(prevCampaigns => 
          prevCampaigns.map(campaign => 
            campaign.campaign_id === campaignId 
              ? { ...campaign, ...updates }
              : campaign
          )
        );
        
        // Mostrar mensaje de éxito
        setOperationSuccess('¡Campaña actualizada exitosamente!');
        
        // Salir del modo edición
        setEditingCampaign(null);
        setEditFormData({});
        setValidationErrors({});
        
        // Limpiar mensaje de éxito después de 3 segundos
        setTimeout(() => {
          setOperationSuccess('');
        }, 3000);
      }
    } catch (err) {
      console.error('Error al actualizar campaña:', err);
      setOperationError('Error al actualizar la campaña: ' + (err.response?.data?.message || err.message));
    } finally {
      setOperationLoading(false);
    }
  };

  // Función para mostrar el modal de confirmación de eliminación
  const showDeleteConfirmation = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
    setOperationError('');
    setOperationSuccess('');
  };

  // Función para cancelar la eliminación
  const cancelDelete = () => {
    setCampaignToDelete(null);
    setShowDeleteModal(false);
    setOperationError('');
    setOperationSuccess('');
  };

  // Función para eliminar una campaña
  const deleteCampaign = async () => {
    if (!campaignToDelete) return;

    setOperationLoading(true);
    setOperationError('');
    setOperationSuccess('');
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/campaigns',
        { 
          headers,
          params: { campaign_id: campaignToDelete.campaign_id }
        }
      );

      if (response.status === 200) {
        // Remover la campaña del estado local
        setCampaigns(prevCampaigns => 
          prevCampaigns.filter(campaign => campaign.campaign_id !== campaignToDelete.campaign_id)
        );
        
        // Mostrar mensaje de éxito
        setOperationSuccess(`¡Campaña "${campaignToDelete.title}" eliminada exitosamente!`);
        
        // Cerrar el modal
        setShowDeleteModal(false);
        setCampaignToDelete(null);
        
        // Limpiar mensaje de éxito después de 3 segundos
        setTimeout(() => {
          setOperationSuccess('');
        }, 3000);
      }
    } catch (err) {
      console.error('Error al eliminar campaña:', err);
      setOperationError('Error al eliminar la campaña: ' + (err.response?.data?.message || err.message));
    } finally {
      setOperationLoading(false);
    }
  };

  // Función para manejar cambios en el formulario de edición
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Validar fechas cuando se cambian
      if (field === 'start_date' || field === 'end_date') {
        validateDates(newData);
      }
      
      return newData;
    });
  };
  
  // Función para validar fechas
  const validateDates = (formData) => {
    const errors = { ...validationErrors };
    
    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date * 1000);
      const endDate = new Date(formData.end_date * 1000);
      
      if (startDate >= endDate) {
        errors.dates = 'La fecha de inicio debe ser menor que la fecha de fin';
      } else {
        delete errors.dates;
      }
    } else {
      delete errors.dates;
    }
    
    setValidationErrors(errors);
  };
  
  // Funciones para formatear moneda en edición
  const formatCurrencyInput = (amount) => {
    if (!amount || amount === 0) return '';
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const parseCurrencyInput = (value) => {
    if (!value) return 0;
    // Remover puntos y comas de separadores de miles
    const cleanValue = value.toString().replace(/[.,]/g, '');
    return parseInt(cleanValue) || 0;
  };
  
  // Funciones para manejar dispositivos
  const addDevice = () => {
    const currentCampaign = getOriginalCampaign(editingCampaign);
    const newDevice = { name: '', bonus_amount: 0 };
    
    // Agregar código de bono si es necesario
    if (currentCampaign?.bonus_type === 'asignar_por_modelo') {
      newDevice.bonus_code = '';
    }
    
    setEditFormData(prev => ({
      ...prev,
      applicable_devices: [...prev.applicable_devices, newDevice]
    }));
  };
  
  const removeDevice = (index) => {
    setEditFormData(prev => ({
      ...prev,
      applicable_devices: prev.applicable_devices.filter((_, i) => i !== index)
    }));
  };
  
  const updateDevice = (index, field, value) => {
    setEditFormData(prev => {
      const newDevices = [...prev.applicable_devices];
      if (field === 'bonus_amount') {
        newDevices[index] = {
          ...newDevices[index],
          [field]: parseCurrencyInput(value)
        };
      } else {
        newDevices[index] = {
          ...newDevices[index],
          [field]: value
        };
      }
      return {
        ...prev,
        applicable_devices: newDevices
      };
    });
  };

  // Función para convertir fecha dd/mm/yyyy a timestamp
  const dateToTimestamp = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    const date = new Date(year, month - 1, day);
    return date.getTime() / 1000; // Convertir a segundos
  };

  // Función para convertir timestamp a fecha dd/mm/yyyy
  const timestampToDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Función para obtener colecciones de bonos disponibles
  const fetchBonusCollections = async () => {
    setLoadingBonusCollections(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        { 
          headers,
          params: { mode: 'all_collections' }
        }
      );
      
      const collectionsData = response.data.collections;
      console.log('🎁 [Campaigns] Collections data received:', collectionsData);
      
      // Transformar el objeto en array si es necesario
      let collectionsArray = [];
      if (Array.isArray(collectionsData)) {
        collectionsArray = collectionsData;
      } else if (collectionsData && typeof collectionsData === 'object') {
        collectionsArray = Object.keys(collectionsData).map(collectionId => ({
          id: collectionId,
          name: collectionsData[collectionId].collection_name
        }));
      }
      
      // Ordenar las colecciones por nombre en orden ascendente
      collectionsArray.sort((a, b) => {
        return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
      });
      
      setBonusCollections(collectionsArray);
    } catch (error) {
      console.error('Error al obtener colecciones de bonos:', error);
      setBonusCollections([]);
    } finally {
      setLoadingBonusCollections(false);
    }
  };

  return (
    <div className="campaigns-container">
      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Gestión de Campañas</div>
        <div className="user-menu">
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>Volver al Dashboard</button>
        </div>
      </header>

      <div className="container">
        <div className="card animate-fade-in">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div>
              <h2 className="card-title" style={{ marginBottom: '0.25rem' }}>Gestión de Campañas</h2>
              <p className="card-subtitle">Administra las campañas de bonos promocionales.</p>
            </div>
            <button 
              className="btn btn-primary" 
              onClick={handleCreateCampaign}
              disabled={loading || isLoadingMore}
            >
              Crear Nueva Campaña
            </button>
          </div>

          {/* Estado de carga inicial */}
          {loading && (
            <div className="loading-container" style={{ minHeight: '200px' }}>
              <div className="loading-spinner"></div>
              <p>Cargando campañas...</p>
            </div>
          )}

          {/* Estado de error */}
          {error && !loading && (
            <div className="message message-error" role="alert" style={{ minHeight: '200px' }}>
              {error}
            </div>
          )}

          {/* Contenido: Tabla de campañas */}
          {!loading && !error && (
            <>
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Descripción</th>
                      <th>Inicio</th>
                      <th>Fin</th>
                      <th>Empresa</th>
                      <th>Marca</th>
                      <th>Categoría</th>
                      <th>Tipo de Bono</th>
                      <th>Colección de Bonos</th>
                      <th>Dispositivos</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.length > 0 ? (
                      campaigns.map((campaign, index) => {
                        const campaignId = campaign.campaign_id || `campaign-${index}`;
                        const isExpanded = !!expandedRows[campaignId];
                        const hasDevices = campaign.applicable_devices && campaign.applicable_devices.length > 0;
                        
                        const isEditing = editingCampaign === campaignId;
                        const originalCampaign = getOriginalCampaign(campaignId);
                        
                        return (
                          <React.Fragment key={campaignId}>
                            <tr 
                              onClick={(e) => {
                                // No expandir si se hace clic en botones de acción o campos de edición
                                if (e.target.closest('.action-buttons') || e.target.closest('.edit-field')) {
                                  return;
                                }
                                if (hasDevices && !isEditing) {
                                  toggleRowExpansion(campaignId);
                                }
                              }}
                              style={{ 
                                cursor: hasDevices && !isEditing ? 'pointer' : 'default', 
                                background: isExpanded ? '#f5f8ff' : isEditing ? '#fff8f0' : 'inherit',
                                fontWeight: isExpanded ? 'bold' : 'normal'
                              }}
                            > 
                              <td style={{ verticalAlign: 'middle' }}>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    className="edit-field"
                                    value={editFormData.title || ''}
                                    onChange={(e) => handleEditFormChange('title', e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      border: `1px solid ${brandIdentity.colors.primary}`,
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      fontFamily: brandIdentity.typography.fontFamily
                                    }}
                                    placeholder="Título de la campaña"
                                  />
                                ) : (
                                  campaign.title || '-'
                                )}
                              </td>
                              <td style={{ maxWidth: '400px', whiteSpace: 'normal', verticalAlign: 'middle' }}>
                                {isEditing ? (
                                  <textarea
                                    className="edit-field"
                                    value={editFormData.description || ''}
                                    onChange={(e) => handleEditFormChange('description', e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      border: `1px solid ${brandIdentity.colors.primary}`,
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      fontFamily: brandIdentity.typography.fontFamily,
                                      minHeight: '60px',
                                      resize: 'vertical'
                                    }}
                                    placeholder="Descripción de la campaña"
                                  />
                                ) : (
                                  campaign.description || '-'
                                )}
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                {isEditing ? (
                                  <input
                                    type="date"
                                    className="edit-field"
                                    value={editFormData.start_date ? new Date(editFormData.start_date * 1000).toISOString().split('T')[0] : ''}
                                    onChange={(e) => {
                                      const timestamp = e.target.value ? new Date(e.target.value).getTime() / 1000 : '';
                                      handleEditFormChange('start_date', timestamp);
                                    }}
                                    style={{
                                      padding: '8px',
                                      border: `1px solid ${validationErrors.dates ? '#ef4444' : brandIdentity.colors.primary}`,
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      fontFamily: brandIdentity.typography.fontFamily
                                    }}
                                  />
                                ) : (
                                  formatDate(campaign.start_date)
                                )}
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                {isEditing ? (
                                  <input
                                    type="date"
                                    className="edit-field"
                                    value={editFormData.end_date ? new Date(editFormData.end_date * 1000).toISOString().split('T')[0] : ''}
                                    onChange={(e) => {
                                      const timestamp = e.target.value ? new Date(e.target.value).getTime() / 1000 : '';
                                      handleEditFormChange('end_date', timestamp);
                                    }}
                                    style={{
                                      padding: '8px',
                                      border: `1px solid ${validationErrors.dates ? '#ef4444' : brandIdentity.colors.primary}`,
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      fontFamily: brandIdentity.typography.fontFamily
                                    }}
                                  />
                                ) : (
                                  formatDate(campaign.end_date)
                                )}
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                {campaign.applicable_company || '-'}
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                {campaign.applicable_brand || '-'}
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                {campaign.applicable_category || '-'}
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                {campaign.bonus_type === 'asignar_por_modelo' ? 'Asignar por modelo' : 
                                 campaign.bonus_type === 'asignar_catalogo_precios' ? 'Asignar catálogo por precios' : 
                                 campaign.bonus_type || '-'}
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                {isEditing && campaign.bonus_type === 'asignar_catalogo_precios' ? (
                                  <select
                                    className="edit-field"
                                    value={editFormData.bonus_collection_id || ''}
                                    onChange={(e) => {
                                      const selectedCollection = bonusCollections.find(c => c.id === e.target.value);
                                      handleEditFormChange('bonus_collection_id', e.target.value);
                                      handleEditFormChange('bonus_collection_name', selectedCollection ? selectedCollection.name : '');
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      border: `1px solid ${brandIdentity.colors.primary}`,
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      fontFamily: brandIdentity.typography.fontFamily
                                    }}
                                  >
                                    <option value="">Seleccionar colección...</option>
                                    {bonusCollections.map((collection) => (
                                      <option key={collection.id} value={collection.id}>
                                        {collection.name}
                                      </option>
                                    ))}
                                  </select>
                                ) : campaign.bonus_type === 'asignar_catalogo_precios' ? 
                                  (campaign.bonus_collection_name || '-') : 
                                  '-'
                                }
                              </td>
                              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                {hasDevices ? (
                                  <span style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    background: '#e6effd', 
                                    borderRadius: '50%', 
                                    width: '24px', 
                                    height: '24px',
                                    color: '#0066cc',
                                    fontWeight: 'bold'
                                  }}>
                                    {campaign.applicable_devices.length}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="action-buttons" style={{ minWidth: '120px', textAlign: 'center', verticalAlign: 'middle', padding: '12px 8px' }}>
                                {isEditing ? (
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', minHeight: '40px', alignItems: 'center' }}>
                                    <button
                                      onClick={() => updateCampaign(campaignId)}
                                      disabled={operationLoading}
                                      style={{
                                        background: brandIdentity.colors.secondary,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        fontWeight: brandIdentity.typography.weights.medium,
                                        cursor: operationLoading ? 'not-allowed' : 'pointer',
                                        opacity: operationLoading ? 0.6 : 1
                                      }}
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      disabled={operationLoading}
                                      style={{
                                        background: brandIdentity.colors.textLight,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        fontWeight: brandIdentity.typography.weights.medium,
                                        cursor: operationLoading ? 'not-allowed' : 'pointer',
                                        opacity: operationLoading ? 0.6 : 1
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', minHeight: '40px', alignItems: 'center' }}>
                                    <button
                                      onClick={() => startEditing(campaign, campaignId)}
                                      disabled={editingCampaign !== null || operationLoading}
                                      style={{
                                        background: brandIdentity.colors.primary,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        fontWeight: brandIdentity.typography.weights.medium,
                                        cursor: (editingCampaign !== null || operationLoading) ? 'not-allowed' : 'pointer',
                                        opacity: (editingCampaign !== null || operationLoading) ? 0.6 : 1
                                      }}
                                      title="Editar campaña"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      onClick={() => showDeleteConfirmation(campaign)}
                                      disabled={editingCampaign !== null || operationLoading}
                                      style={{
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        fontWeight: brandIdentity.typography.weights.medium,
                                        cursor: (editingCampaign !== null || operationLoading) ? 'not-allowed' : 'pointer',
                                        opacity: (editingCampaign !== null || operationLoading) ? 0.6 : 1
                                      }}
                                      title="Eliminar campaña"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                            
                            {/* Fila expandida para mostrar dispositivos */}
                            {((isExpanded && hasDevices) || isEditing) && (
                              <tr>
                                <td colSpan="9" style={{ padding: '0' }}>
                                  <div style={{
                                    padding: '1rem',
                                    backgroundColor: isEditing ? '#fff8f0' : '#f5f8ff',
                                    borderTop: '1px solid #e0e0e0',
                                    animation: 'fadeIn 0.3s ease-in-out'
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                      <h4 style={{ margin: 0, color: '#333' }}>Dispositivos Aplicables</h4>
                                      {isEditing && (
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                          <button
                                            onClick={addDevice}
                                            style={{
                                              background: brandIdentity.colors.secondary,
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: '8px 16px',
                                              fontSize: '14px',
                                              fontWeight: brandIdentity.typography.weights.medium,
                                              cursor: 'pointer',
                                              fontFamily: brandIdentity.typography.fontFamily
                                            }}
                                          >
                                            + Agregar Dispositivo
                                          </button>
                                          <button
                                            onClick={() => {
                                              // Mostrar modal de confirmación primero
                                              setEditingCampaignExcel(campaignId);
                                              setShowConfirmExcelModal(true);
                                            }}
                                            style={{
                                              background: brandIdentity.colors.primary,
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: '8px 16px',
                                              fontSize: '14px',
                                              fontWeight: brandIdentity.typography.weights.medium,
                                              cursor: 'pointer',
                                              fontFamily: brandIdentity.typography.fontFamily
                                            }}
                                          >
                                            📊 Cargar desde Excel
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Vista en modo edición */}
                                    {isEditing ? (
                                      <div style={{ marginTop: '15px' }}>
                                        {/* Headers de columnas */}
                                        <div style={{ 
                                          display: 'grid', 
                                          gridTemplateColumns: originalCampaign?.bonus_type === 'asignar_por_modelo' ? '2fr 1.5fr 1.5fr auto' : '2fr 1.5fr auto',
                                          gap: '10px', 
                                          marginBottom: '10px',
                                          padding: '8px',
                                          backgroundColor: '#e9ecef',
                                          borderRadius: '4px',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          color: '#495057'
                                        }}>
                                          <div>Nombre del Dispositivo</div>
                                          <div>Valor del Bono</div>
                                          {originalCampaign?.bonus_type === 'asignar_por_modelo' && <div>Código del Bono</div>}
                                          <div>Acciones</div>
                                        </div>

                                        {/* Lista de dispositivos */}
                                        {editFormData.applicable_devices.map((device, idx) => (
                                          <div key={idx} style={{
                                            display: 'grid',
                                            gridTemplateColumns: originalCampaign?.bonus_type === 'asignar_por_modelo' ? '2fr 1.5fr 1.5fr auto' : '2fr 1.5fr auto',
                                            gap: '10px',
                                            alignItems: 'center',
                                            marginBottom: '10px',
                                            padding: '12px',
                                            backgroundColor: 'white',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '6px'
                                          }}>
                                            <input
                                              type="text"
                                              value={device.name}
                                              onChange={(e) => updateDevice(idx, 'name', e.target.value)}
                                              placeholder="Nombre del dispositivo"
                                              style={{
                                                margin: 0,
                                                width: '100%',
                                                padding: '8px',
                                                border: `1px solid ${brandIdentity.colors.primary}`,
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                fontFamily: brandIdentity.typography.fontFamily
                                              }}
                                            />
                                            <div style={{ position: 'relative' }}>
                                              <span style={{
                                                position: 'absolute',
                                                left: '8px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: brandIdentity.colors.textLight,
                                                fontSize: '14px',
                                                pointerEvents: 'none',
                                                zIndex: 1
                                              }}>$</span>
                                              <input
                                                type="text"
                                                value={formatCurrencyInput(device.bonus_amount)}
                                                onChange={(e) => {
                                                  // Permitir solo números, puntos y comas
                                                  const value = e.target.value.replace(/[^\d.,]/g, '');
                                                  updateDevice(idx, 'bonus_amount', value);
                                                }}
                                                placeholder="0"
                                                style={{
                                                  margin: 0,
                                                  width: '100%',
                                                  padding: '8px 8px 8px 20px',
                                                  border: `1px solid ${brandIdentity.colors.primary}`,
                                                  borderRadius: '4px',
                                                  fontSize: '14px',
                                                  fontFamily: brandIdentity.typography.fontFamily,
                                                  textAlign: 'right'
                                                }}
                                              />
                                            </div>
                                            {originalCampaign?.bonus_type === 'asignar_por_modelo' && (
                                              <input
                                                type="text"
                                                value={device.bonus_code || ''}
                                                onChange={(e) => updateDevice(idx, 'bonus_code', e.target.value)}
                                                placeholder="Código del bono"
                                                style={{
                                                  margin: 0,
                                                  width: '100%',
                                                  padding: '8px',
                                                  border: `1px solid ${brandIdentity.colors.primary}`,
                                                  borderRadius: '4px',
                                                  fontSize: '14px',
                                                  fontFamily: brandIdentity.typography.fontFamily
                                                }}
                                              />
                                            )}
                                            {editFormData.applicable_devices.length > 1 && (
                                              <button
                                                onClick={() => removeDevice(idx)}
                                                style={{
                                                  width: '40px',
                                                  height: '40px',
                                                  borderRadius: '50%',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  background: '#ef4444',
                                                  color: 'white',
                                                  border: 'none',
                                                  fontSize: '14px',
                                                  cursor: 'pointer',
                                                  fontFamily: brandIdentity.typography.fontFamily
                                                }}
                                                title="Eliminar dispositivo"
                                              >
                                                ✕
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        {editFormData.applicable_devices.length === 0 && (
                                          <div style={{
                                            padding: '2rem',
                                            textAlign: 'center',
                                            color: brandIdentity.colors.textLight,
                                            fontStyle: 'italic'
                                          }}>
                                            No hay dispositivos. Haz clic en "Agregar Dispositivo" para agregar uno.
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      /* Vista normal (solo lectura) */
                                      <div>
                                        {/* Headers de columnas para vista de solo lectura */}
                                        <div style={{ 
                                          display: 'grid', 
                                          gridTemplateColumns: campaign.bonus_type === 'asignar_por_modelo' ? '2fr 1.5fr 1.5fr' : '2fr 1.5fr',
                                          gap: '10px', 
                                          marginBottom: '10px',
                                          padding: '8px',
                                          backgroundColor: '#e9ecef',
                                          borderRadius: '4px',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          color: '#495057'
                                        }}>
                                          <div>Nombre del Dispositivo</div>
                                          <div>Valor del Bono</div>
                                          {campaign.bonus_type === 'asignar_por_modelo' && <div>Código del Bono</div>}
                                        </div>
                                        
                                        {/* Lista de dispositivos en vista de solo lectura */}
                                        {campaign.applicable_devices.map((device, idx) => (
                                          <div key={idx} style={{
                                            display: 'grid',
                                            gridTemplateColumns: campaign.bonus_type === 'asignar_por_modelo' ? '2fr 1.5fr 1.5fr' : '2fr 1.5fr',
                                            gap: '10px',
                                            alignItems: 'center',
                                            marginBottom: '10px',
                                            padding: '12px',
                                            backgroundColor: 'white',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '6px'
                                          }}>
                                            <span style={{ fontWeight: 'normal' }}>{device.name}</span>
                                            <span style={{ 
                                              color: '#007c4e', 
                                              fontWeight: 'bold', 
                                              background: '#e6f5ed', 
                                              padding: '0.25rem 0.5rem', 
                                              borderRadius: '4px',
                                              textAlign: 'center',
                                              display: 'inline-block'
                                            }}>
                                              {formatCurrency(device.bonus_amount)}
                                            </span>
                                            {campaign.bonus_type === 'asignar_por_modelo' && (
                                              <span style={{
                                                color: '#6366f1',
                                                fontWeight: 'bold',
                                                background: '#f0f0ff',
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '4px',
                                                textAlign: 'center',
                                                display: 'inline-block',
                                                fontFamily: 'monospace'
                                              }}>
                                                {device.bonus_code || '-'}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="no-results">No se encontraron campañas.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginación: Botón "Cargar más" */}
              {nextToken && (
                <div className="pagination" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  <button 
                    className={`btn btn-secondary ${isLoadingMore ? 'btn-loading' : ''}`} 
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? 'Cargando...' : 'Cargar más campañas'}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Mostrar errores de operación */}
          {operationError && (
            <div className="message message-error" role="alert" style={{ marginTop: '1rem' }}>
              {operationError}
            </div>
          )}
          
          {/* Mostrar mensajes de éxito */}
          {operationSuccess && (
            <div className="message message-success" role="alert" style={{ marginTop: '1rem' }}>
              {operationSuccess}
            </div>
          )}
          
          {/* Mostrar errores de validación */}
          {validationErrors.dates && (
            <div className="message message-error" role="alert" style={{ marginTop: '1rem' }}>
              {validationErrors.dates}
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación para eliminación */}
      {showDeleteModal && campaignToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: brandIdentity.colors.white,
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px',
            width: '90%',
            fontFamily: brandIdentity.typography.fontFamily
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                backgroundColor: '#fee2e2',
                borderRadius: '50%',
                padding: '12px',
                marginRight: '1rem'
              }}>
                <span style={{
                  fontSize: '24px',
                  color: '#dc2626'
                }}>
                  ⚠️
                </span>
              </div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: brandIdentity.typography.sizes.h2,
                  fontWeight: brandIdentity.typography.weights.semibold,
                  color: brandIdentity.colors.textDark,
                  marginBottom: '0.5rem'
                }}>
                  Confirmar Eliminación
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: brandIdentity.typography.sizes.body,
                  color: brandIdentity.colors.textLight,
                  lineHeight: 1.5
                }}>
                  ¿Estás seguro de que deseas eliminar la campaña <strong>"{campaignToDelete.title}"</strong>?
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
            
            {operationError && (
              <div className="message message-error" role="alert" style={{ marginBottom: '1rem' }}>
                {operationError}
              </div>
            )}
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={cancelDelete}
                disabled={operationLoading}
                style={{
                  background: 'transparent',
                  color: brandIdentity.colors.textLight,
                  border: `1px solid ${brandIdentity.colors.textLight}`,
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: brandIdentity.typography.sizes.body,
                  fontWeight: brandIdentity.typography.weights.medium,
                  cursor: operationLoading ? 'not-allowed' : 'pointer',
                  opacity: operationLoading ? 0.6 : 1,
                  fontFamily: brandIdentity.typography.fontFamily
                }}
              >
                Cancelar
              </button>
              <button
                onClick={deleteCampaign}
                disabled={operationLoading}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: brandIdentity.typography.sizes.body,
                  fontWeight: brandIdentity.typography.weights.medium,
                  cursor: operationLoading ? 'not-allowed' : 'pointer',
                  opacity: operationLoading ? 0.6 : 1,
                  fontFamily: brandIdentity.typography.fontFamily
                }}
              >
                {operationLoading ? 'Eliminando...' : 'Eliminar Campaña'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para cargar Excel */}
      {showConfirmExcelModal && editingCampaignExcel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: brandIdentity.colors.white,
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px',
            width: '90%',
            fontFamily: brandIdentity.typography.fontFamily
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                backgroundColor: '#fef3cd',
                borderRadius: '50%',
                padding: '12px',
                marginRight: '1rem'
              }}>
                <span style={{
                  fontSize: '24px',
                  color: '#f59e0b'
                }}>
                  ⚠️
                </span>
              </div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: brandIdentity.typography.sizes.h2,
                  fontWeight: brandIdentity.typography.weights.semibold,
                  color: brandIdentity.colors.textDark,
                  marginBottom: '0.5rem'
                }}>
                  Reemplazar Dispositivos
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: brandIdentity.typography.sizes.body,
                  color: brandIdentity.colors.textLight,
                  lineHeight: 1.5
                }}>
                  Al cargar datos desde Excel, <strong>se borrarán todos los dispositivos actuales</strong> y se reemplazarán por los nuevos datos del archivo.
                </p>
                <p style={{
                  margin: '0.75rem 0 0 0',
                  fontSize: brandIdentity.typography.sizes.body,
                  color: brandIdentity.colors.textLight,
                  lineHeight: 1.5
                }}>
                  ¿Deseas continuar?
                </p>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowConfirmExcelModal(false);
                  setEditingCampaignExcel(null);
                }}
                style={{
                  background: 'transparent',
                  color: brandIdentity.colors.textLight,
                  border: `1px solid ${brandIdentity.colors.textLight}`,
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: brandIdentity.typography.sizes.body,
                  fontWeight: brandIdentity.typography.weights.medium,
                  cursor: 'pointer',
                  fontFamily: brandIdentity.typography.fontFamily
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowConfirmExcelModal(false);
                  setShowExcelModal(true);
                }}
                style={{
                  background: brandIdentity.colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: brandIdentity.typography.sizes.body,
                  fontWeight: brandIdentity.typography.weights.medium,
                  cursor: 'pointer',
                  fontFamily: brandIdentity.typography.fontFamily
                }}
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Excel para Dispositivos en Edición */}
      {showExcelModal && editingCampaignExcel && (
        <SimpleExcelDeviceModal
          isOpen={showExcelModal}
          onClose={() => {
            setShowExcelModal(false);
            setEditingCampaignExcel(null);
          }}
          onFinalize={(devices) => {
            // Reemplazar dispositivos de la campaña que se está editando
            setEditFormData(prev => ({
              ...prev,
              applicable_devices: devices
            }));
            setShowExcelModal(false);
            setEditingCampaignExcel(null);
          }}
        />
      )}

      {/* Añadir estilos CSS para animación */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .edit-field:focus {
          outline: none;
          border-color: ${brandIdentity.colors.primaryLight} !important;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
        }
        
        .action-buttons button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .action-buttons button:active:not(:disabled) {
          transform: translateY(0);
        }
        
        /* Arreglar líneas divisoras de la tabla */
        .users-table {
          border-collapse: collapse;
          width: 100%;
        }
        
        .users-table td {
          border-bottom: 1px solid #e0e0e0;
        }
        
        .users-table th {
          border-bottom: 2px solid #e0e0e0;
        }
        
        /* Asegurar que todos los botones tengan la misma altura que el contenido */
        .action-buttons > div {
          min-height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
}

export default Campaigns; 